// Single source of truth for reconciling a חשבונית מרכזת (consolidated invoice)
// against its linked תעודות משלוח / החזרה. The comparison is on NET amounts
// (subtotal), NOT gross totals.
//
// Why net: a תעודת משלוח is not a tax document — the VAT is charged on the
// consolidated invoice, not on the individual delivery notes (some suppliers
// are VAT-exempt or partial-VAT on the delivery-note side entirely). Comparing
// gross totals would show a phantom "difference" equal to the invoice VAT.
// Comparing net keeps both sides on the same basis and removes VAT rounding
// noise. Shared by the admin link modal (LinkDeliveryNotesModal) and the
// customer-facing invoice view (DocumentViewPage) so both show the same result.

const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

// A תעודת החזרה (return) reduces what the consolidating invoice covers, so its
// net subtracts — mirrors the CREDIT_TYPES handling on the supplier page.
export function signedNet(doc) {
  const net = Math.abs(parseFloat(doc?.subtotal) || 0);
  return doc?.doc_type === 'תעודת החזרה' ? -net : net;
}

// Residual below which the reconciliation is treated as matched — accumulated
// per-line rounding and minor price differences across a month of delivery
// notes are expected. max(₪10, 0.1% of invoice net): a fixed floor for small
// invoices, scaling gently for large ones. A genuine pricing/quantity error
// larger than this still surfaces as a mismatch.
export function reconcileTolerance(invoiceNet) {
  return Math.max(10, Math.abs(parseFloat(invoiceNet) || 0) * 0.001);
}

// Compare the net sum of the linked delivery/return notes to the invoice net.
// Returns the two net figures, the signed diff (linked − invoice), whether it
// is within tolerance, and the tolerance itself (for display).
export function reconcileNet(links, invoiceSubtotal) {
  const linkedNet  = round2((links || []).reduce((s, l) => s + signedNet(l), 0));
  const invoiceNet = round2(invoiceSubtotal);
  const diff       = round2(linkedNet - invoiceNet);
  const tolerance  = reconcileTolerance(invoiceNet);
  const matches    = Math.abs(diff) <= tolerance;
  return { linkedNet, invoiceNet, diff, matches, tolerance };
}

// The link modal's running totals: the selected notes, plus the notes the
// invoice names that are not in the system. `missing_total` is already signed (a
// missing return reduces it) and holds the amounts AS PRINTED on the invoice.
// With no printed amount at all there is no sum to add: withMissing is null, so
// the screen says so instead of showing a total that quietly leaves them out.
// Same when the admin confirmed the invoice by amount: nothing is really missing.
export function linkTotals(linkedNet, coverage, confirmed = false) {
  const missingCount = confirmed ? 0 : (coverage?.missing?.length ?? 0);
  if (missingCount === 0) return { linkedNet, missingCount: 0, missingNet: 0, unpricedCount: 0, withMissing: linkedNet };
  const unpricedCount = coverage.missing_without_amount ?? 0;
  const missingNet = Number(coverage.missing_total) || 0;
  return {
    linkedNet,
    missingCount,
    missingNet,
    unpricedCount,
    withMissing: unpricedCount === missingCount ? null : Math.round((linkedNet + missingNet) * 100) / 100,
  };
}

// What is left between the selected notes and the invoice — the one number the
// admin is actually working towards. Counted after the missing notes when their
// amounts are known: a gap the red notes explain is not a finding of its own.
// 'numbering': the selection alone closes the sums while references still read
// as missing, usually the same notes under another numbering. Adding the missing
// on top would count those notes twice, so there is no gap to show.
export function linkGap(totals, invoiceNet) {
  const net = parseFloat(invoiceNet) || 0;
  if (net <= 0) return null;
  const tolerance = reconcileTolerance(net);
  const selectionDiff = round2(totals.linkedNet - net);
  if (Math.abs(selectionDiff) <= tolerance) {
    return totals.missingCount > 0
      ? { kind: 'numbering', amount: 0 }
      : { kind: 'match', amount: Math.abs(selectionDiff) };
  }
  if (totals.missingCount > 0 && totals.withMissing != null
      && Math.abs(totals.withMissing - net) <= tolerance) {
    return { kind: 'explained', amount: 0 };
  }
  const diff = round2((totals.withMissing ?? totals.linkedNet) - net);
  return { kind: diff < 0 ? 'short' : 'over', amount: Math.abs(diff) };
}
